import React, { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { Tooltip, Badge, Spin } from "antd";
import {
  MessageOutlined,
  CloseOutlined,
  SendOutlined,
  RobotOutlined,
  TeamOutlined,
  BookOutlined,
  PhoneOutlined,
  BulbOutlined,
  UserOutlined,
  RiseOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";

const MAX_INPUT_LENGTH = 500;
const MAX_CHAT_HISTORY = 50;

// ===== VERIFIED FACTS SCRAPED FROM HTTPS://BJSINDIA.ORG =====
const WELCOME_MESSAGE =
  "Namaste! 🙏 I'm the BJS Assistant. Ask me about BJS mission, programs, disaster response, WERC, founder, volunteer opportunities, or contact details.";

const ABOUT_BJS_RESPONSE =
  "Bharatiya Jain Sanghatana (BJS) is a non-profit social impact organization founded in 1985 in Pune. BJS is at the forefront of addressing national concerns in disaster response, water conservation, education, healthcare, and social reform through 500+ chapters and 40,000+ dedicated volunteers across India.\n\n🌐 Website: https://bjsindia.org";

const VISION_MISSION_RESPONSE =
  "🌟 BJS Vision & Mission:\n\n• Vision: To collectively contribute towards Nation Building through Holistic Development of Society.\n\n• Mission: Identify major societal concerns, formulate meaningful solutions, and implement them by leveraging professional expertise and a nationwide volunteer network.";

const FOUNDER_RESPONSE =
  "👤 Our Founder:\nShri Shantilal Gulabchand Muttha founded BJS in 1985 at the age of 31. Born on 15 August 1954 in Dongarkinhi and an alumnus of BMCC Pune, he pioneered mass marriages (Parichay Sammelans) to eradicate dowry practices and founded the Wagholi Educational Rehabilitation Center (WERC) after the 1993 Latur earthquake.\n\n🌐 Read more: https://bjsindia.org/BJS-India/founder.html";

const CONTACT_RESPONSE =
  "📍 BJS Head Office:\nLevel 3, Muttha Chambers II, Senapati Bapat Marg, Pune – 411016 (Maharashtra), India\n\n📞 Phone: +91 20 66050220\n📧 Email: info@bjsindia.org\n🌐 Website: https://bjsindia.org\n🤝 Volunteer Portal: https://nexus.bjsindia.org/";

const VOLUNTEER_RESPONSE =
  "Volunteers are the backbone of BJS! With over 40,000+ volunteers across 500+ chapters, you can make a tangible grassroots impact in education, water conservation, and disaster response.\n\n👉 Register online: https://nexus.bjsindia.org/ or connect with your local BJS chapter.";

const IMPACT_RESPONSE =
  "📊 BJS Impact at a Glance:\n• 40+ Years of Nation Building (Est. 1985)\n• 10 Lakh+ Lives Touched across India\n• 31+ Transformative Programs Scaled\n• 40,000+ Volunteers Mobilized\n• 500+ Active Chapters Nationwide\n• Lead Player in Disaster Relief since 1993";

const WERC_RESPONSE =
  "🏫 Wagholi Educational Rehabilitation Center (WERC), Pune:\nEstablished post-1993 Latur earthquake by Shri Shantilal Muttha, WERC is a permanent institution providing quality education, residential care, and holistic nurturing for disaster-affected, tribal, and underprivileged children across India.";

const DISASTER_RESPONSE =
  "🚨 Disaster Response & Relief:\nBJS has led major relief & rehabilitation operations across India since the 1993 Latur Earthquake, 2001 Gujarat Earthquake, and the COVID-19 pandemic (operating Mobile Dispensary Seva 'Doctor at your doorstep', Mega Blood Donation drives, and emergency medical relief).\n\n🌐 Details: https://bjsindia.org/legacy.html";

const SMART_GIRL_RESPONSE =
  "👧 Smart Girl Program:\nBJS's flagship empowerment initiative for adolescent girls, focusing on self-awareness, emotional resilience, decision-making, and self-defense. Implemented in partnership with Zilla Parishad schools and private institutions across Maharashtra and other states.\n\n🌐 Details: https://bjsindia.org/BJS-India/smart-girl-zilla-parishad.html";

const MULYAVARDHAN_RESPONSE =
  "📚 Mulyavardhan:\nA school-based value education initiative developed by the Shantilal Muttha Foundation / BJS, aimed at cultivating constitutional values, democratic principles, critical thinking, and social-emotional competencies among primary school students.";

const WATER_RESPONSE =
  "💧 Water Conservation & RWB (Rejuvenation of Water Bodies):\nBJS works aggressively on drought mitigation through desilting dams, rejuvenating river basins, and creating water storage capacity across drought-prone districts in Maharashtra and Karnataka.";

const TRUSTEES_RESPONSE =
  "👥 BJS Key Trustees & Leadership:\n• Shri Shantilal Muttha (Founder)\n• Shri Vallabh Bhanshali\n• Shri Arun Nahar\n• Shri Rajendra Lunker\n• Shri Sudarshan Jain\n• Ms. Neha Muttha\n• Ms. Sonali Chordia\n\n🌐 View Trustees: https://bjsindia.org/BJS-India/BJSTrustees.html";

// Strip HTML tags for clean rendering
const stripHtml = (html) => {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  } catch (e) {
    return html.replace(/<[^>]*>?/gm, "").trim();
  }
};

// Helper to render text with clickable URLs and Emails
const renderMessageWithLinks = (text, isUser = false) => {
  if (!text) return null;

  const tokenRegex = /(https?:\/\/[^\s<]+|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  const parts = text.split(tokenRegex);

  return parts.map((part, index) => {
    if (!part) return null;

    if (/^https?:\/\//i.test(part)) {
      let cleanUrl = part;
      let trailing = "";
      if (/[.,;:)]$/.test(cleanUrl)) {
        trailing = cleanUrl.slice(-1);
        cleanUrl = cleanUrl.slice(0, -1);
      }
      return (
        <React.Fragment key={index}>
          <a
            href={cleanUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: isUser ? "#ffffff" : "#1d4ed8",
              textDecoration: "underline",
              fontWeight: 600,
              wordBreak: "break-all",
            }}
          >
            {cleanUrl}
          </a>
          {trailing}
        </React.Fragment>
      );
    }

    if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i.test(part)) {
      return (
        <a
          key={index}
          href={`mailto:${part}`}
          style={{
            color: isUser ? "#ffffff" : "#1d4ed8",
            textDecoration: "underline",
            fontWeight: 600,
          }}
        >
          {part}
        </a>
      );
    }

    return part;
  });
};

const createInitialChatHistory = () => [
  {
    id: "welcome-1",
    sender: "bot",
    text: WELCOME_MESSAGE,
    timestamp: Date.now(),
  },
];

// ===== THEME: High-end, executive aesthetic =====
const THEME = {
  primary: "#1d4ed8",
  primaryDark: "#1e40af",
  accent: "#7c3aed",
  light: "#f8fafc",
  border: "#e2e8f0",
  text: "#0f172a",
  textMuted: "#64748b",
  bgGradient: "linear-gradient(135deg, #1d4ed8 0%, #4338ca 50%, #7c3aed 100%)",
  botBubbleBg: "#f1f5f9",
  userBubbleBg: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
  cardBg: "#ffffff",
};

// 6 Core Prompts requested by user
const QUICK_PROMPTS = [
  { text: "Tell me about BJS", icon: <BookOutlined />, color: "#2563eb" },
  { text: "What programs does BJS run?", icon: <BulbOutlined />, color: "#7c3aed" },
  { text: "How can I volunteer?", icon: <TeamOutlined />, color: "#059669" },
  { text: "What's the impact?", icon: <RiseOutlined />, color: "#d97706" },
  { text: "Who founded BJS?", icon: <UserOutlined />, color: "#db2777" },
  { text: "How to contact?", icon: <PhoneOutlined />, color: "#0284c7" },
];

const CHATBOT_ANIMATION_CSS = `
  @keyframes bjsChatbotFadeIn {
    from { opacity: 0; transform: translateY(12px) scale(0.97); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }

  @keyframes bjsPulseGlow {
    0%, 100% { box-shadow: 0 10px 25px -5px rgba(29, 78, 216, 0.4), 0 8px 10px -6px rgba(124, 58, 237, 0.3); }
    50% { box-shadow: 0 20px 35px -5px rgba(29, 78, 216, 0.6), 0 12px 16px -6px rgba(124, 58, 237, 0.5); }
  }

  @keyframes bjsDotPulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.3); opacity: 0.7; }
  }

  .bjs-chatbot-launcher:hover {
    transform: scale(1.08) translateY(-2px);
  }

  .bjs-chatbot-prompt:hover {
    background-color: #f1f5f9 !important;
    border-color: #cbd5e1 !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  }

  .bjs-program-chip {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .bjs-program-chip:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.18);
    border-color: #2563eb !important;
    background: #eff6ff !important;
  }

  .bjs-chatbot-send:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(29, 78, 216, 0.35);
  }
`;

const sanitizeInput = (text) => {
  if (typeof text !== "string") return "";
  return text.trim().slice(0, MAX_INPUT_LENGTH);
};

export default function ChatbotWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState(createInitialChatHistory);
  const [inputValue, setInputValue] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [programsList, setProgramsList] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [selectedProgramId, setSelectedProgramId] = useState(null);

  const historyEndRef = useRef(null);
  const timeoutRef = useRef(null);

  // Auto-scroll to latest message
  useEffect(() => {
    historyEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory, isThinking, loadingPrograms]);

  // Fetch verified programs from API
  const fetchProgramsData = useCallback(async () => {
    if (programsList.length > 0) return programsList;
    try {
      setLoadingPrograms(true);
      const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:8000/api";
      const response = await axios.get(`${baseUrl}/programs`);
      const data = response.data;
      if (Array.isArray(data)) {
        const valid = data.filter((p) => p && p.name && (p.status ? p.status === "ACTIVE" : true));
        const listToUse = valid.length > 0 ? valid : data.filter((p) => p && p.name);
        setProgramsList(listToUse);
        return listToUse;
      }
      return [];
    } catch (err) {
      console.error("[ChatbotWidget] Failed to fetch programs from API:", err);
      return [];
    } finally {
      setLoadingPrograms(false);
    }
  }, [programsList]);

  // Preload programs when widget opens
  useEffect(() => {
    if (isOpen && programsList.length === 0) {
      fetchProgramsData();
    }
  }, [isOpen, fetchProgramsData, programsList.length]);

  // Triggered when user asks about programs or clicks the programs prompt
  const handleProgramsQuery = useCallback(async () => {
    setIsThinking(true);
    let currentPrograms = programsList;
    if (!currentPrograms || currentPrograms.length === 0) {
      currentPrograms = await fetchProgramsData();
    }

    setIsThinking(false);

    if (!currentPrograms || currentPrograms.length === 0) {
      setChatHistory((prev) => [
        ...prev,
        {
          id: `bot-${Date.now()}`,
          sender: "bot",
          text: "BJS drives transformative programs across education, disaster response, social initiatives, and water conservation. Explore all live programs at https://bjsindia.org or connect with your local BJS chapter.",
          timestamp: Date.now(),
        },
      ]);
      return;
    }

    // Add interactive cascaded programs message
    setChatHistory((prev) => [
      ...prev,
      {
        id: `bot-programs-${Date.now()}`,
        sender: "bot",
        text: `BJS operates ${currentPrograms.length} verified programs across India. Select any program below to view its official details from our database:`,
        isProgramSelector: true,
        programs: currentPrograms,
        timestamp: Date.now(),
      },
    ]);
  }, [fetchProgramsData, programsList]);

  // Triggered when user selects a specific program from cascaded inputs
  const handleSelectProgram = useCallback((program) => {
    if (!program) return;
    setSelectedProgramId(program._id);

    const cleanDescription = stripHtml(program.description) || "No detailed description provided yet.";

    setChatHistory((prev) => [
      ...prev,
      {
        id: `user-select-${Date.now()}`,
        sender: "user",
        text: `Tell me about ${program.name}`,
        timestamp: Date.now(),
      },
      {
        id: `bot-detail-${Date.now()}`,
        sender: "bot",
        isProgramDetail: true,
        program: {
          ...program,
          cleanDescription,
        },
        timestamp: Date.now() + 100,
      },
    ]);
  }, []);

  const handleSend = useCallback(async (messageText) => {
    const sanitized = sanitizeInput(messageText);
    if (!sanitized || sanitized.length > MAX_INPUT_LENGTH) {
      return;
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      sender: "user",
      text: sanitized,
      timestamp: Date.now(),
    };

    setChatHistory((prev) => {
      const updated = [...prev, userMessage];
      return updated.length > MAX_CHAT_HISTORY ? updated.slice(-MAX_CHAT_HISTORY) : updated;
    });

    const normalized = sanitized.toLowerCase().replace(/[^\w\s]/g, " ").replace(/\s+/g, " ").trim();

    // Check if query is about programs list
    const isProgramQuery = /^(what programs|programs|initiatives|what programs does bjs run|list programs|all programs|projects)$/i.test(normalized) ||
      (/(program|programs|initiative|initiatives|scheme|schemes|projects)/i.test(sanitized) && !/smart girl|mulyavardhan|water|werc/i.test(sanitized));

    if (isProgramQuery) {
      handleProgramsQuery();
      return;
    }

    // Check if query matches a specific dynamic program name from API
    let matchingProgram = null;
    if (programsList.length > 0) {
      matchingProgram = programsList.find((p) =>
        normalized.includes(p.name.toLowerCase().trim()) || p.name.toLowerCase().trim().includes(normalized)
      );
    }

    if (matchingProgram) {
      handleSelectProgram(matchingProgram);
      return;
    }

    // Standard verified queries using scraped data
    setIsThinking(true);
    timeoutRef.current = window.setTimeout(() => {
      let botAnswer = "";

      if (/smart girl/i.test(normalized)) {
        botAnswer = SMART_GIRL_RESPONSE;
      } else if (/mulyavardhan|value education/i.test(normalized)) {
        botAnswer = MULYAVARDHAN_RESPONSE;
      } else if (/werc|wagholi|rehabilitation center/i.test(normalized)) {
        botAnswer = WERC_RESPONSE;
      } else if (/disaster|earthquake|latur|gujarat|covid|doctor at.*doorstep|relief|blood donation/i.test(normalized)) {
        botAnswer = DISASTER_RESPONSE;
      } else if (/water|rwb|drought|sujalam|desilting|reservoir/i.test(normalized)) {
        botAnswer = WATER_RESPONSE;
      } else if (/trustee|trustees|board|governing|leadership team|director/i.test(normalized)) {
        botAnswer = TRUSTEES_RESPONSE;
      } else if (/vision|mission|goal|objective|aim/i.test(normalized)) {
        botAnswer = VISION_MISSION_RESPONSE;
      } else if (/who founded|founder|shantilal|muttha|history|origin|established|birth/i.test(normalized)) {
        botAnswer = FOUNDER_RESPONSE;
      } else if (/volunteer|join|how can i help|contribute|participate|membership|register/i.test(normalized)) {
        botAnswer = VOLUNTEER_RESPONSE;
      } else if (/impact|achievements|numbers|statistics|scale|how many|metrics/i.test(normalized)) {
        botAnswer = IMPACT_RESPONSE;
      } else if (/contact|email|phone|website|address|reach|office|head office|pune office|location|senapati bapat/i.test(normalized)) {
        botAnswer = CONTACT_RESPONSE;
      } else if (/tell me about bjs|about bjs|what is bjs|who is bjs|about us/i.test(normalized)) {
        botAnswer = ABOUT_BJS_RESPONSE;
      } else {
        botAnswer =
          "I can assist you with verified information from bjsindia.org regarding BJS Mission & Vision, Programs (Smart Girl, Mulyavardhan, Water Conservation), WERC Wagholi, Disaster Relief history, Volunteering (nexus.bjsindia.org), or Head Office contacts. What would you like to explore?";
      }

      setChatHistory((prev) => [
        ...prev,
        {
          id: `bot-${Date.now()}`,
          sender: "bot",
          text: botAnswer,
          timestamp: Date.now(),
        },
      ]);
      setIsThinking(false);
    }, 400);
  }, [handleProgramsQuery, handleSelectProgram, programsList]);

  const handleQuickPrompt = useCallback((promptText) => {
    if (promptText === "What programs does BJS run?") {
      setChatHistory((prev) => [
        ...prev,
        {
          id: `user-${Date.now()}`,
          sender: "user",
          text: promptText,
          timestamp: Date.now(),
        },
      ]);
      handleProgramsQuery();
    } else {
      handleSend(promptText);
    }
  }, [handleProgramsQuery, handleSend]);

  const handleSubmit = useCallback((event) => {
    event.preventDefault();
    const sanitized = sanitizeInput(inputValue);
    if (!sanitized || isThinking) return;

    handleSend(sanitized);
    setInputValue("");
  }, [handleSend, inputValue, isThinking]);

  const handleClose = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsOpen(false);
    setIsThinking(false);
    setInputValue("");
    setChatHistory(createInitialChatHistory());
  }, []);

  const messageCount = Math.max(0, chatHistory.length - 1);

  return (
    <div style={styles.container}>
      <style>{CHATBOT_ANIMATION_CSS}</style>

      {/* Floating launcher button */}
      {!isOpen ? (
        <Tooltip title="Chat with BJS Assistant" placement="left">
          <Badge
            count={messageCount}
            style={{ backgroundColor: THEME.accent, boxShadow: "0 0 0 2px #fff" }}
            overflowCount={99}
          >
            <button
              type="button"
              className="bjs-chatbot-launcher"
              style={styles.launcher}
              onClick={() => setIsOpen(true)}
              aria-label="Open BJS Chat Assistant"
            >
              <div style={styles.launcherGlow} />
              <div style={styles.launcherContent}>
                <RobotOutlined style={{ fontSize: 26, color: "#ffffff" }} />
                <span style={styles.launcherPulseDot} />
              </div>
            </button>
          </Badge>
        </Tooltip>
      ) : (
        /* Chat Window Modal */
        <div style={styles.chatWindow}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerLeft}>
              <div style={styles.avatarWrap}>
                <div style={styles.avatar}>
                  <RobotOutlined style={{ fontSize: 18, color: "#ffffff" }} />
                </div>
                <span style={styles.onlineBadge} />
              </div>
              <div style={styles.headerInfo}>
                <h3 style={styles.headerTitle}>BJS Assistant</h3>
                <span style={styles.headerSubtitle}>
                  <span style={styles.statusDot} />
                  Live Official Assistant
                </span>
              </div>
            </div>
            <button
              type="button"
              style={styles.closeButton}
              onClick={handleClose}
              aria-label="Close chat"
              title="Close chat"
            >
              <CloseOutlined style={{ fontSize: 15 }} />
            </button>
          </div>

          {/* Chat Messages Body */}
          <div style={styles.chatBody}>
            {chatHistory.map((item) => (
              <div
                key={item.id}
                style={{
                  ...styles.messageWrap,
                  justifyContent: item.sender === "user" ? "flex-end" : "flex-start",
                }}
              >
                {/* Bot Icon */}
                {item.sender === "bot" && (
                  <div style={styles.messageAvatar}>
                    <RobotOutlined style={{ fontSize: 13, color: THEME.primary }} />
                  </div>
                )}

                <div
                  style={{
                    ...styles.messageBubble,
                    ...(item.sender === "user" ? styles.userBubble : styles.botBubble),
                  }}
                >
                  {/* Standard Message Text with Clickable Links */}
                  {item.text && (
                    <div style={{ whiteSpace: "pre-line", lineHeight: 1.55 }}>
                      {renderMessageWithLinks(item.text, item.sender === "user")}
                    </div>
                  )}

                  {/* Cascaded Programs Selector Chips / Cards */}
                  {item.isProgramSelector && item.programs && (
                    <div style={styles.programGrid}>
                      <div style={styles.programGridHeader}>
                        <AppstoreOutlined style={{ color: THEME.primary, marginRight: 6 }} />
                        <span style={{ fontWeight: 600, fontSize: 12, color: THEME.text }}>
                          Select an Official BJS Program:
                        </span>
                      </div>
                      <div style={styles.chipsContainer}>
                        {item.programs.map((prog) => (
                          <button
                            key={prog._id || prog.name}
                            type="button"
                            className="bjs-program-chip"
                            style={{
                              ...styles.programChip,
                              borderColor: selectedProgramId === prog._id ? THEME.primary : "#e2e8f0",
                              background: selectedProgramId === prog._id ? "#eff6ff" : "#ffffff",
                            }}
                            onClick={() => handleSelectProgram(prog)}
                          >
                            <span style={styles.programChipDot} />
                            <span style={styles.programChipText}>{prog.name}</span>
                            <ArrowRightOutlined style={{ fontSize: 10, color: THEME.textMuted, marginLeft: "auto" }} />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Program Detail Card */}
                  {item.isProgramDetail && item.program && (
                    <div style={styles.programDetailCard}>
                      <div style={styles.detailHeader}>
                        <div style={styles.detailIconWrap}>
                          <BulbOutlined style={{ fontSize: 16, color: "#ffffff" }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={styles.detailTitle}>{item.program.name}</div>
                          {item.program.programDayMonth && (
                            <div style={styles.detailMeta}>
                              <CalendarOutlined style={{ marginRight: 4 }} />
                              Celebrated on: {item.program.programDayMonth}
                            </div>
                          )}
                        </div>
                        <span style={styles.verifiedBadge}>
                          <CheckCircleOutlined style={{ marginRight: 3 }} /> Verified
                        </span>
                      </div>

                      <div style={styles.detailDivider} />

                      <div style={styles.detailDescription}>
                        {renderMessageWithLinks(item.program.cleanDescription, false)}
                      </div>

                      <div style={styles.detailFooter}>
                        <button
                          type="button"
                          style={styles.moreProgramsBtn}
                          onClick={handleProgramsQuery}
                        >
                          <ReloadOutlined style={{ marginRight: 5 }} />
                          View All Programs
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Timestamp */}
                  <span
                    style={{
                      ...styles.timestamp,
                      color: item.sender === "user" ? "rgba(255,255,255,0.7)" : THEME.textMuted,
                      textAlign: item.sender === "user" ? "right" : "left",
                    }}
                  >
                    {new Date(item.timestamp).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              </div>
            ))}

            {/* Thinking / Loading Indicator */}
            {(isThinking || loadingPrograms) && (
              <div style={{ ...styles.messageWrap, justifyContent: "flex-start" }}>
                <div style={styles.messageAvatar}>
                  <RobotOutlined style={{ fontSize: 13, color: THEME.primary }} />
                </div>
                <div style={{ ...styles.messageBubble, ...styles.botBubble, display: "flex", alignItems: "center", gap: 8, padding: "10px 16px" }}>
                  <Spin size="small" />
                  <span style={{ fontSize: 12.5, color: THEME.textMuted }}>
                    {loadingPrograms ? "Connecting to BJS database..." : "BJS Assistant is thinking..."}
                  </span>
                </div>
              </div>
            )}

            <div ref={historyEndRef} />
          </div>

          {/* Quick Prompts - 6 Core Questions */}
          <div style={styles.promptsContainer}>
            <div style={styles.promptsHeader}>
              <span>Suggested Questions</span>
            </div>
            <div style={styles.quickPromptsGrid}>
              {QUICK_PROMPTS.map((prompt) => (
                <button
                  key={prompt.text}
                  type="button"
                  className="bjs-chatbot-prompt"
                  style={styles.promptButton}
                  onClick={() => handleQuickPrompt(prompt.text)}
                  disabled={isThinking || loadingPrograms}
                  title={prompt.text}
                >
                  <span
                    style={{
                      ...styles.promptIconWrap,
                      background: `${prompt.color}15`,
                      color: prompt.color,
                    }}
                  >
                    {prompt.icon}
                  </span>
                  <span style={styles.promptText}>{prompt.text}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Input Form Footer */}
          <form style={styles.inputForm} onSubmit={handleSubmit}>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              maxLength={MAX_INPUT_LENGTH}
              placeholder="Ask about BJS mission, programs, volunteering..."
              disabled={isThinking || loadingPrograms}
              style={styles.chatInput}
              aria-label="Type your question"
            />
            <button
              type="submit"
              className="bjs-chatbot-send"
              disabled={isThinking || loadingPrograms || !inputValue.trim()}
              style={{
                ...styles.sendButton,
                ...((isThinking || loadingPrograms || !inputValue.trim()) ? styles.sendButtonDisabled : {}),
              }}
              aria-label="Send message"
            >
              <SendOutlined style={{ fontSize: 15 }} />
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

// ===== STYLES =====
const styles = {
  container: {
    position: "fixed",
    bottom: 24,
    right: 24,
    zIndex: 9999,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  launcher: {
    width: 62,
    height: 62,
    borderRadius: "50%",
    background: THEME.bgGradient,
    border: "none",
    cursor: "pointer",
    boxShadow: "0 10px 25px -5px rgba(29, 78, 216, 0.5), 0 8px 10px -6px rgba(124, 58, 237, 0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
    position: "relative",
  },
  launcherGlow: {
    position: "absolute",
    inset: -2,
    borderRadius: "50%",
    background: "linear-gradient(135deg, rgba(255,255,255,0.4), transparent)",
    pointerEvents: "none",
  },
  launcherContent: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  launcherPulseDot: {
    position: "absolute",
    top: -2,
    right: -2,
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: "#10b981",
    border: "2px solid #ffffff",
  },
  chatWindow: {
    width: 390,
    height: 620,
    maxHeight: "calc(100vh - 48px)",
    backgroundColor: "#ffffff",
    borderRadius: 20,
    boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    animation: "bjsChatbotFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1)",
  },
  header: {
    background: THEME.bgGradient,
    padding: "14px 18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: "#ffffff",
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  avatarWrap: {
    position: "relative",
  },
  avatar: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1.5px solid rgba(255, 255, 255, 0.4)",
  },
  onlineBadge: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 11,
    height: 11,
    borderRadius: "50%",
    backgroundColor: "#10b981",
    border: "2px solid #ffffff",
  },
  headerInfo: {
    display: "flex",
    flexDirection: "column",
  },
  headerTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 700,
    color: "#ffffff",
    letterSpacing: "-0.2px",
  },
  headerSubtitle: {
    fontSize: 11.5,
    color: "rgba(255, 255, 255, 0.9)",
    display: "flex",
    alignItems: "center",
    gap: 5,
    marginTop: 1,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: "#10b981",
    display: "inline-block",
  },
  closeButton: {
    background: "rgba(255, 255, 255, 0.15)",
    border: "none",
    color: "#ffffff",
    cursor: "pointer",
    width: 30,
    height: 30,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.2s ease",
  },
  chatBody: {
    flex: 1,
    padding: "16px 14px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    backgroundColor: "#f8fafc",
  },
  messageWrap: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    width: "100%",
  },
  messageAvatar: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    backgroundColor: "#e0e7ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginBottom: 4,
  },
  messageBubble: {
    maxWidth: "85%",
    padding: "10px 14px",
    borderRadius: 16,
    fontSize: 13,
    wordBreak: "break-word",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
  },
  botBubble: {
    backgroundColor: "#ffffff",
    color: THEME.text,
    borderBottomLeftRadius: 4,
    border: "1px solid #e2e8f0",
  },
  userBubble: {
    background: THEME.userBubbleBg,
    color: "#ffffff",
    borderBottomRightRadius: 4,
  },
  timestamp: {
    fontSize: 10,
    marginTop: 2,
    display: "block",
  },
  programGrid: {
    marginTop: 8,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    width: "100%",
  },
  programGridHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: 4,
  },
  chipsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    maxHeight: 210,
    overflowY: "auto",
    paddingRight: 2,
  },
  programChip: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    cursor: "pointer",
    textAlign: "left",
    width: "100%",
  },
  programChipDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    backgroundColor: THEME.primary,
    flexShrink: 0,
  },
  programChipText: {
    fontSize: 12,
    fontWeight: 600,
    color: THEME.text,
    flex: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  programDetailCard: {
    marginTop: 4,
    background: "#ffffff",
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    padding: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
  },
  detailHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  detailIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: THEME.bgGradient,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  detailTitle: {
    fontSize: 13.5,
    fontWeight: 700,
    color: THEME.text,
  },
  detailMeta: {
    fontSize: 11,
    color: THEME.textMuted,
    marginTop: 1,
    display: "flex",
    alignItems: "center",
  },
  verifiedBadge: {
    fontSize: 10,
    fontWeight: 600,
    color: "#059669",
    background: "#ecfdf5",
    padding: "2px 6px",
    borderRadius: 6,
    border: "1px solid #a7f3d0",
    display: "flex",
    alignItems: "center",
  },
  detailDivider: {
    height: 1,
    background: "#f1f5f9",
    margin: "8px 0",
  },
  detailDescription: {
    fontSize: 12.5,
    color: "#334155",
    lineHeight: 1.5,
    maxHeight: 180,
    overflowY: "auto",
  },
  detailFooter: {
    marginTop: 10,
    display: "flex",
    justifyContent: "flex-end",
  },
  moreProgramsBtn: {
    background: "#f8fafc",
    border: "1px solid #cbd5e1",
    padding: "4px 10px",
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 500,
    color: THEME.primary,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  },
  promptsContainer: {
    backgroundColor: "#ffffff",
    borderTop: "1px solid #e2e8f0",
    padding: "8px 12px",
  },
  promptsHeader: {
    fontSize: 10.5,
    fontWeight: 600,
    color: THEME.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 6,
  },
  quickPromptsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 6,
  },
  promptButton: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 8px",
    borderRadius: 8,
    border: "1px solid #f1f5f9",
    background: "#f8fafc",
    cursor: "pointer",
    textAlign: "left",
    transition: "all 0.15s ease",
  },
  promptIconWrap: {
    width: 22,
    height: 22,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    flexShrink: 0,
  },
  promptText: {
    fontSize: 11,
    fontWeight: 500,
    color: THEME.text,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  inputForm: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    backgroundColor: "#ffffff",
    borderTop: "1px solid #e2e8f0",
  },
  chatInput: {
    flex: 1,
    padding: "9px 14px",
    borderRadius: 20,
    border: "1px solid #cbd5e1",
    fontSize: 12.5,
    outline: "none",
    backgroundColor: "#f8fafc",
    transition: "border-color 0.2s ease, background 0.2s ease",
  },
  sendButton: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: THEME.bgGradient,
    border: "none",
    color: "#ffffff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    flexShrink: 0,
  },
  sendButtonDisabled: {
    opacity: 0.45,
    cursor: "not-allowed",
    transform: "none",
  },
};
